import { yDocToProsemirrorJSON } from 'y-prosemirror'
import { generateHTML, generateJSON } from '@tiptap/html'
import rehypeParse from 'rehype-parse'
import rehypeStringify from 'rehype-stringify'
import { unified } from 'unified'
import { convert } from 'html-to-text'
import * as Y from 'yjs'
import { isEmpty } from './is-empty'
import type { Extensions } from '@tiptap/react'
import { z } from 'zod'
import rehypeSlug from 'rehype-slug'
import rehypeToc from 'rehype-toc'
import { toHtml } from 'hast-util-to-html'
import { invariant } from '@epic-web/invariant'
import { Document } from '@tiptap/extension-document'
import { Paragraph } from '@tiptap/extension-paragraph'
import Ol from '@tiptap/extension-ordered-list'
import Li from '@tiptap/extension-list-item'
import { Text } from '@tiptap/extension-text'
import { stateToJson } from './plain-text-to-ydoc'
import { flattenTipTapNodes } from './flatten-nodes'
import { BaseHubBlock, BaseHubInlineBlock, Link } from './rich-text-extensions'
import { transformationsCache } from './_cache'
import { hash } from '@basehub/utils/hash'

export function initServerTipTap({ value }: { value: string }) {
  const ydoc = new Y.Doc()
  const unit8array = Buffer.from(value, 'base64')
  try {
    Y.applyUpdateV2(ydoc, unit8array)
  } catch (error) {
    Y.applyUpdate(ydoc, unit8array)
  }

  const json = yDocToProsemirrorJSON(ydoc, 'content')

  return { ydoc, json, isEmpty: isEmpty(json) }
}

export function tipTapJSONToHTMLString({
  json,
  extensions,
}: Pick<ReturnType<typeof initServerTipTap>, 'isEmpty' | 'json'> & {
  extensions: Extensions
}) {
  return generateHTML(json, extensions)
}

export async function enhanceGeneratedHTML({
  html,
  options,
}: {
  html: string
  options: Partial<{
    slugs: boolean
    toc:
      | true
      | {
          /** Render the table of contents inside a \<nav \/\> */
          navWrapper: boolean
          /** Render the table of contents and exclude the rest of the content */
          excludeMainContent: boolean
        }
  }>
}) {
  const processor = unified()
  let tocHtml: string

  const plugins = [
    [rehypeParse, { fragment: true }],
    options.slugs && [rehypeSlug],
    options.toc && [
      rehypeToc,
      {
        nav: typeof options.toc !== 'boolean' && options.toc.navWrapper,
        ...(typeof options.toc !== 'boolean' && options.toc.excludeMainContent
          ? {
              customizeTOC: function (toc: any) {
                tocHtml = toHtml(toc)
                return false
              },
            }
          : {}),
      },
    ],
    [rehypeStringify],
  ]

  plugins.filter(Boolean).forEach((plugin) => {
    // @ts-expect-error Plugins are defined here
    processor.use(plugin[0], plugin[1])
  })

  const htmlWithToc = await processor.process(html)

  if (typeof options.toc !== 'boolean' && options.toc?.excludeMainContent) {
    // @ts-expect-error tocHtml is defined in the customizeTOC function
    invariant(tocHtml, 'BSHB: tocHtml should be defined after processing')
    // Ensure TOC has at least one <li> element
    if (/<li(?:\s|>)/.test(tocHtml)) {
      return String(tocHtml)
    }
    return null
  }
  return String(htmlWithToc)
}

// Cache the converted HTMLs to avoid converting the same HTML multiple times,
// for example to retrieve the reading time
const plainTextByBlockHash = new Map<string, string>()

export async function tipTapHTMLStringToPlainText({ html }: { html: string }) {
  const hashKey = await hash(html)
  if (!plainTextByBlockHash.has(hashKey)) {
    plainTextByBlockHash.set(
      hashKey,
      convert(html, {
        formatters: {},
        selectors: [
          {
            selector: 'a',
            options: {
              ignoreHref: true,
            },
          },
          {
            selector: 'img',
            format: 'skip',
          },
          {
            selector: 'table',
            format: 'skip',
          },
          {
            selector: 'h1',
            options: { uppercase: false },
          },
          {
            selector: 'h2',
            options: { uppercase: false },
          },
          {
            selector: 'h3',
            options: { uppercase: false },
          },
        ],
      })
    )
  }

  return z.string().parse(plainTextByBlockHash.get(hashKey))
}

export function generateJsonFromHTML(html: string | null) {
  if (!html) return null

  return generateJSON(html, [Document, Paragraph, Link, Text, Ol, Li])
}

export const getInstancesInRichText = (
  richText: string,
  prefetchedJson?: ReturnType<typeof stateToJson>,
  opts?: { cacheKey?: string }
): string[] => {
  if (opts?.cacheKey) {
    const cached = transformationsCache.get(
      'get-instances-in-rich-text' + opts.cacheKey
    )
    if (cached) return cached as string[]
  }

  const instanceIds = new Set<string>()

  const json = prefetchedJson ?? stateToJson(richText, opts)
  const flattenedNodes = flattenTipTapNodes(json.content as any)
  flattenedNodes.forEach((node) => {
    if (node.type === BaseHubBlock.name) {
      instanceIds.add(node.attrs!.id)
    } else if (node.marks) {
      node.marks.forEach((mark) => {
        if (mark?.attrs?.id) {
          instanceIds.add(mark.attrs.id)
        }
      })
    }
  })
  return [...instanceIds]
}

export const getInstancesAndInternalLinksInRichText = (
  richText: string,
  prefetchedJson?: ReturnType<typeof stateToJson>,
  opts?: { cacheKey?: string }
): string[] => {
  if (opts?.cacheKey) {
    const cached = transformationsCache.get(
      'get-instances-in-rich-text' + opts.cacheKey
    )
    if (cached) return cached as string[]
  }

  const instanceIds = new Set<string>()

  const json = prefetchedJson ?? stateToJson(richText, opts)
  const flattenedNodes = flattenTipTapNodes(json.content as any)
  flattenedNodes.forEach((node) => {
    if (node.type === BaseHubBlock.name && node.attrs) {
      instanceIds.add(node.attrs.id)
    } else if (node.marks) {
      node.marks.forEach((mark) => {
        if (!mark.attrs) return
        if (mark.type === BaseHubInlineBlock.name && mark.attrs.id) {
          instanceIds.add(mark.attrs.id)
        } else if (mark.type === Link.name && mark.attrs.targetId) {
          instanceIds.add(mark.attrs.targetId)
        }
      })
    }
  })

  return [...instanceIds]
}
